/* eslint-disable react-refresh/only-export-components */
// 同文件导出组件 + 常量/辅助函数为有意模式;react-refresh 仅关注 HMR,不影响运行时。
/**
 * 画风(L1)+ 配色(L2)面板 — 2026-06-01 重构(紧凑入口 + 画廊弹窗)/ 2026-07-25 V0 分层
 *
 * 旧版是一排 ~21 个纯文字 chip,28 风格 + 样例图后会撑成文字墙且用户无概念。改为:
 *   侧栏紧凑 = [自由发挥] + 业务核心横滑缩略图 + [全部 39 种风格] + [自定义]
 *   [全部风格] → StyleGalleryDialog(分组 + 搜索 + 样例图网格)
 *
 * ═══ V0(定案 3.2.4 命名纪律 + §4.3)═══════════════════════════════════════════
 * 标题「风格」→**「画风」**:舱内原来有 5 套东西都叫「风格」(画风 / 配色 / 区块排法 /
 * 后期滤镜 / 字体档),同一个词指不同的东西。这一层只管**画面的味道**,叫画风。
 * 同时新增「配色」行(L2,`StyleColorRow`),字体档从本面板底部移进那一行(定案 3.2.1)。
 *
 * 风格数据 / tone / helper 收口到 ./styleOptions;本文件 re-export 保持
 * VideoBriefPanel / VideoCreatePage 等老 import 不破。
 *
 * 选中后 tone 写入 normalize context.user_style + 出图 prompt【风格】段(单一事实源)。
 * "自由发挥"(默认) → tone=null,AI 据行业气质推导。
 */
import { useState } from "react";
import { Grid3x3, Pencil, Sparkles } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { Textarea } from "@/shared/ui/textarea";
import { cn } from "@/shared/lib/utils";
import { PosterSectionTitle } from "@/shared/ui/PosterCard";
import { StyleThumb } from "../../../ui/components/free-create/StyleThumb";
import { StyleGalleryDialog } from "../../../ui/components/free-create/StyleGalleryDialog";
import {
  findStyleByTone,
  isCustomTone,
  PRESET_STYLES,
} from "../../../ui/components/free-create/styleOptions";
import { StyleColorRow } from "../../../ui/components/free-create/StyleColorRow";
import type { StyleTokens } from "../../../longimage/model/canvas";

// Re-export 数据 / 类型 / helper,保持老调用方 import 路径不变(VideoBriefPanel /
// useFreeCreateActions / VideoCreatePage 仍从 StylePanel 取)。
export {
  STYLES,
  PRESET_STYLES,
  STYLE_GROUPS,
  FREE_STYLE,
  isCustomTone,
  styleLabelForTone,
  findStyleByTone,
  type StyleOption,
} from "../../../ui/components/free-create/styleOptions";

interface Props {
  selectedStyle: string | null;
  setSelectedStyle: (v: string | null) => void;
  /** #20·28 字体风格档 key(fontStyleOptions),默认 FONT_STYLE_AUTO。V0 起渲染在「配色」行里。 */
  fontStyle: string;
  setFontStyle: (v: string) => void;
  // ───── V0 L2「配色」(定案 3.2.2)
  styleTokens: StyleTokens | null;
  setStyleTokens: (v: StyleTokens | null) => void;
  followStyle: boolean;
  resetFollowStyle: () => void;
}

const BUSINESS_STYLES = PRESET_STYLES.filter((s) => s.group === "business");

export function StylePanel({
  selectedStyle,
  setSelectedStyle,
  fontStyle,
  setFontStyle,
  styleTokens,
  setStyleTokens,
  followStyle,
  resetFollowStyle,
}: Props) {
  const customActive = isCustomTone(selectedStyle);
  const isFree = selectedStyle === null;
  const selectedPreset = findStyleByTone(selectedStyle);

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [draft, setDraft] = useState(customActive ? (selectedStyle ?? "") : "");
  const [customOpen, setCustomOpen] = useState(false);

  // 热门横排:[选中的非业务预设(若有)] + 业务核心 9。让画廊里选的创意/长尾风格也能在
  // 侧栏显示为选中态,不至于"选了看不见"。
  const popular =
    selectedPreset && selectedPreset.group !== "business"
      ? [selectedPreset, ...BUSINESS_STYLES]
      : BUSINESS_STYLES;

  // 2026-07-30 —— 去卡片化:这一段是**辅助层**,不该和大框一样重。
  // ★原来是 `CreateSection`(卡) 里套 `PosterCard`(卡) 两层壳 + 两级标题,
  //   于是「① 画成什么样」比大框那句「想要一张什么样的图?」还显眼 —— 层级反了。
  //   现在:无边框、标题降到 12.5px、内容直接铺开。一屏内只留一个重元素(大框)。
  return (
    <div className="space-y-2" data-style-band>
      <div className="pb-0.5">
        {/* 命名纪律 3.2.4:这一层叫「画风」——「风格」以前同时指五样东西 */}
        <PosterSectionTitle
          title="画风"
          hint="画面的味道。选中后 AI 优化与出图直接采用;不选 = 自由发挥(AI 据内容判断);也可「自定义」自由描述"
        />
      </div>

      <div className="space-y-3">
        {/* 热门横滑:自由发挥 + 业务核心缩略图 */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {/* 自由发挥(无样例图,sparkle tile) */}
          <button
            type="button"
            onClick={() => setSelectedStyle(null)}
            aria-pressed={isFree}
            title="自由发挥 — 由 AI 据内容判断调性"
            className={cn(
              // ★`justify-center`:这张卡没有 4:5 样例图,内容只有「图标 + 一行字」,
              //   而它在行里被拉到与旁边缩略图同高 —— 不居中的话图标顶在上沿,
              //   一排看过去就它偏上。
              "flex w-24 shrink-0 flex-col items-center justify-center gap-1.5 rounded-lg border py-2 transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              isFree
                ? "border-primary bg-primary/5 ring-2 ring-primary"
                : "border-border/60 hover:border-primary/50"
            )}
          >
            <span
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full",
                isFree ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
              )}
            >
              <Sparkles className="h-4 w-4" />
            </span>
            <span
              className={cn(
                "text-sm font-medium",
                isFree ? "text-primary" : "text-muted-foreground"
              )}
            >
              自由发挥
            </span>
          </button>

          {popular.map((s) => (
            <StyleThumb
              key={s.key}
              option={s}
              selected={selectedStyle === s.tone}
              onSelect={() => setSelectedStyle(s.tone)}
              size="sm"
              className="w-24 shrink-0"
            />
          ))}
        </div>

        {/* 全部风格 + 自定义 */}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 flex-1 cursor-pointer gap-1.5 text-[13px]"
            onClick={() => setGalleryOpen(true)}
          >
            <Grid3x3 className="h-3.5 w-3.5" />
            全部 39 种风格
          </Button>

          <Popover open={customOpen} onOpenChange={setCustomOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant={customActive ? "default" : "outline"}
                size="sm"
                className="h-8 cursor-pointer gap-1.5 text-[13px]"
                aria-pressed={customActive}
              >
                <Pencil className="h-3.5 w-3.5" />
                {customActive ? "自定义已设" : "自定义"}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 space-y-2 p-3">
              <p className="text-[13px] font-medium text-foreground">自定义风格</p>
              <p className="text-2xs text-muted-foreground">
                用自己的话描述想要的画风(如:暖色手绘插画,日系小清新,留白多)。会作为风格提示交给 AI。
              </p>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="例:复古胶片质感,低饱和暖色,颗粒感,文艺氛围"
                rows={3}
                maxLength={300}
                aria-label="自定义风格描述"
                className="resize-none text-sm"
              />
              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 cursor-pointer px-2 text-[13px]"
                  onClick={() => {
                    setDraft("");
                    if (customActive) setSelectedStyle(null);
                    setCustomOpen(false);
                  }}
                >
                  清除
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-7 cursor-pointer px-3 text-[13px]"
                  disabled={!draft.trim()}
                  onClick={() => {
                    setSelectedStyle(draft.trim());
                    setCustomOpen(false);
                  }}
                >
                  应用
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* 自定义已设时的回显 */}
        {customActive && (
          <p className="line-clamp-2 rounded-md bg-muted/50 px-2.5 py-1.5 text-[12.5px] text-muted-foreground">
            自定义风格:{selectedStyle}
          </p>
        )}

        {/* L2 配色 + 字体档(定案 3.2.1:字体从这里移进配色行,一处选择) */}
        <StyleColorRow
          styleTokens={styleTokens}
          setStyleTokens={setStyleTokens}
          followStyle={followStyle}
          resetFollowStyle={resetFollowStyle}
          fontStyle={fontStyle}
          setFontStyle={setFontStyle}
        />
      </div>

      <StyleGalleryDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        // 自定义激活时传哨兵(不等于任何 tone 也不等于 null),避免误高亮「自由发挥」。
        selectedStyle={customActive ? "__custom__" : selectedStyle}
        onSelect={(tone) => setSelectedStyle(tone)}
      />
    </div>
  );
}
